import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { type CronRunDTO, useLazyListCronRunsQuery } from '../api/cronApi';

interface CronRunHistoryProps {
  scheduleName: string;
}

const statusColors: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  queued: 'warning',
  published: 'success',
  failed: 'error',
  running: 'warning',
  skipped: 'default',
};

function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export default function CronRunHistory({ scheduleName }: CronRunHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [trigger, { data, isLoading, error }] = useLazyListCronRunsQuery();

  const handleToggle = () => {
    if (!expanded && !data) {
      trigger({ name: scheduleName, limit: 20 });
    }
    setExpanded(!expanded);
  };

  const runs = data?.runs ?? [];

  return (
    <Box>
      <Button
        size="small"
        onClick={handleToggle}
        startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none' }}
      >
        Run History
      </Button>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, pl: 1 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading history...
              </Typography>
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Failed to load run history
            </Alert>
          )}
          {!isLoading && runs.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No runs recorded yet
            </Typography>
          )}
          {runs.map((run) => (
            <CronRunItem key={run.id} run={run} />
          ))}
          {runs.length >= 20 && (
            <Button
              size="small"
              onClick={() => trigger({ name: scheduleName, limit: 50 })}
              sx={{ mt: 1 }}
            >
              Load more
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function CronRunItem({ run }: { run: CronRunDTO }) {
  const startedAt = new Date(run.started_at);
  const fullTime = format(startedAt, 'MMM d, h:mm:ss a');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" sx={{ minWidth: 140 }} title={fullTime}>
        {fullTime}
      </Typography>
      <Chip
        size="small"
        label={run.status}
        color={statusColors[run.status] || 'default'}
        sx={{ minWidth: 80 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
        {formatDuration(run.duration_ms)}
      </Typography>
      <Chip size="small" label={run.trigger_type} variant="outlined" sx={{ minWidth: 80 }} />
      {run.error_message && (
        <Typography variant="body2" color="error" sx={{ flex: 1 }}>
          {run.error_message}
        </Typography>
      )}
    </Box>
  );
}
